import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/calendar/calendar.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import { ListAspectSchema } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import type { Product } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import { CommentsAPI } from '@chancenwerk/shared/comments/api/CommentsAPI.js';
import { Comment } from '@chancenwerk/shared/comments/entities/Comment.js';

import '@chancenwerk/client/components/chancen-component-card-title.js';
import '@chancenwerk/client/components/chancen-component-context-menu.js';
import type { ContextMenuItem } from '@chancenwerk/client/components/chancen-component-context-menu.js';
import '@chancenwerk/client/components/chancen-component-fullscreen-card.js';
import '@chancenwerk/client/components/chancen-component-icon-spoiler.js';
import '@chancenwerk/client/components/chancen-component-info-icon.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { Weekday } from '@chancenwerk/enums/enums.js';
import { CapacityAPI } from '@chancenwerk/shared/capacity/api/CapacityAPI.js';
import { CapacityGuestDay } from '@chancenwerk/shared/capacity/entities/CapacityGuestDay.js';
import {
  CapacityRequest,
  CapacityRequestStatus,
  CapacityRequestType,
} from '@chancenwerk/shared/capacity/entities/CapacityRequest.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { Role } from '@chancenwerk/shared/semantics/Role.js';
import '@chancenwerk/client/design/components/cr-dropdown-search-selection.js';
import { ChromiumEntityVersionPatchList } from '@chancenwerk/client/design/components/cr-entity-version-patch-list.js';
import '@chancenwerk/client/design/components/cr-nospace-toggle-content.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-textarea.js';
import { KPF, KPF_BLACKLIST } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import type { School } from '@chancenwerk/shared/master-data-management/partner/entities/School.js';
import { EmployeeAPI } from '@chancenwerk/shared/master-data-management/users/api/EmployeeAPI.js';
import { PERSON_BLACKLIST } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { ChancenPersonalConfigAPI } from '@chancenwerk/shared/user-configs/api/ChancenPersonalConfigAPI.js';
import { ListUIConfig } from '@chancenwerk/shared/user-configs/entities/ListUIConfig.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { DateTime } from 'luxon';
import { of, switchMap, tap } from 'rxjs';
import { KraefteDialogEditCapacity } from '../components/dialog/kraefte-dialog-edit-capacity.js';
import '../components/kraefte-component-capacity-courses-popup.js';
import { filterPatches, formatPatchValue } from '../patch-utils.js';

const schoolSelector: BaseQLSelectionSet<School> = {
  name: 1,
  address: { zipCity: { zip: 1, city: 1 }, strasse: 1, country: 1 },
  bundesland: 1,
  standort: 1,
  kuerzel: 1,
};

const kpfSelector: BaseQLSelectionSet<KPF> = { school: schoolSelector, produkt: { name: 1, kuerzel: 1 }, kuerzel: 1 };

const mitarbeiterSelector: BaseQLSelectionSet<Mitarbeiter> = {
  _id: 1,
  vorname: 1,
  nachname: 1,
  handynummer: 1,
  handynummerChancenwerk: 1,
  email: 1,
  emailChancenwerk: 1,
  guestAccount: 1,
  workAvailabilities: { weekday: 1, from: 1, to: 1 },
  vertraege: {
    startDatum: 1,
    vertragsart: 1,
    taetigkeiten: { startDate: 1, endDate: 1, preliminaryEndDate: 1, subjectID: 1, predicateID: 1, objectID: 1 },
  },
  adresse: { zipCity: { zip: 1, city: 1 }, strasse: 1, country: 1 },
};

const filterSelector: BaseQLSelectionSet<AdornisFilter> = {
  ...AdornisFilter.allFields,
  aspects: { ...AbstractAspect.allFields, value: 1, zipCity: { zip: 1, city: 1 } },
};

/**
 * @customElement kraefte-page-capacity-management
 */
@customElement('kraefte-page-capacity-management')
export class KraeftePageCapacityManagement extends CrPage {
  @state() private readonly _translation = new TranslationController(this);

  @property({ attribute: false }) private readonly kpfID = new RXController(this, '');

  @state() private readonly _config = new RXController(
    this,
    ChancenPersonalConfigAPI.getByKeyAndCategory(
      ListUIConfig<typeof CapacityRequest>,
      'kraefte-capacity-request',
      'default',
    )({
      key: 1,
      userID: 1,
      category: 1,
      view: {
        filter: filterSelector,
        mode: 1,
        configuredColumns: { customID: 1, fieldName: 1, columnWidth: 1 },
        selectedItems: 1,
        multiSelection: 1,
      },
    }),
  );

  @state() private readonly _selectedKPF = new RXController(
    this,
    this.kpfID.observable.pipe(
      switchMap(id => {
        if (!id) {
          return of(null);
        }
        return KPF.getByID<KPF<Product>>(id)(kpfSelector);
      }),
      tap(kpf => {
        if (kpf) {
          this.label = kpf.kuerzel;
        }
      }),
    ),
  );

  @state() private readonly _schukos = new RXController(
    this,
    this.kpfID.observable.pipe(
      switchMap(async id => {
        if (!id) {
          return of(null);
        }
        return EmployeeAPI.getWithTaetigkeit(id, Role.SCHUKO, DateTime.now())(mitarbeiterSelector);
      }),
    ),
  );

  @state() private readonly _stbs = new RXController(
    this,
    this.kpfID.observable.pipe(
      switchMap(async id => {
        if (!id) {
          return of(null);
        }
        return EmployeeAPI.getWithTaetigkeit(id, Role.INTERNAL_KPF_OPERATIV, DateTime.now())(mitarbeiterSelector);
      }),
    ),
  );

  @state() private readonly _referenceIDs = new RXController(
    this,
    this.kpfID.observable.pipe(
      switchMap(async id => {
        if (!id) {
          return of(null);
        }
        return CapacityAPI.getReferenceIDsForKPF(id)();
      }),
    ),
  );

  constructor() {
    super();
    this.permission = ChancenPermission.kraefte.LOGIN_KRAEFTE;
    this.label = 'Bedarfsplanung';
    this.backRoute = '/';
  }

  override header() {
    return html`
      <chancen-component-context-menu
        .items=${[
          {
            icon: 'info',
            label: 'Benachrichtigung als gelesen markieren',
            action: async () => {
              await CapacityAPI.setNotify(this._selectedKPF.value?._id ?? '', false)();
              XSnackbar.show('Benachrichtigung als gelesen markiert');
            },
          },
        ] satisfies ContextMenuItem[]}
      ></chancen-component-context-menu>
    `;
  }

  override content() {
    if (!this._selectedKPF.value) {
      return html`<cr-flex padding="lg" crossaxis-center><cr-loader></cr-loader></cr-flex>`;
    }
    return this._kpfOverview();
  }

  private _smallerHeader(text: string) {
    return () => {
      return html`<cr-text bold ${css({ padding: this.spacing.xs })}>${text}</cr-text>`;
    };
  }

  private async _showGuestDayDialog(guestDay?: CapacityGuestDay) {
    return ChromiumDialog.showPopup<Maybe<CapacityGuestDay>>({
      content: (resolve, reject, dialogElement) => {
        const form = new GenericFieldFormController(
          this,
          guestDay ?? new CapacityGuestDay({ KPFID: this._selectedKPF.value?._id }),
          {
            onSuccess: async doc => {
              if (doc._id) {
                await doc.save();
              } else {
                await doc.create();
              }
              resolve(doc);
            },
          },
        );

        return html`<cr-flex space="md" padding="md" ${css({ width: '500px' })}>
          <x-entity-picker
            flex
            placeholder="Bedarf"
            clearable
            ${form.field('requestId')}
            .options=${{
              class: CapacityRequest,
              selectionSet: { fromWhen: 1, weekdays: 1, type: 1 },
              filter: new AdornisFilter({
                aspects: [
                  new StringAspect({ fieldName: 'status', mode: 'containsNONE', value: CapacityRequestStatus.HIRED }),
                  new StringAspect({ fieldName: 'KPFID', mode: 'equals', value: this.kpfID.value }),
                ],
              }),
            }}
            .renderString=${(e: Maybe<CapacityRequest>) => {
              if (!e) {
                return '';
              }
              return `${e?.type}, ${e?.fromWhen?.toFormat('dd.MM.yyyy')}, ${e?.weekdays?.join(', ')}`;
            }}
          ></x-entity-picker>
          <generated-form .formController=${form} .blacklist=${['KPFID', 'createdAt', 'requestId']}></generated-form>
          <cr-flex horizontal crossaxis-center space="md">
            <cr-button @click=${() => form.validate()}>Speichern</cr-button>
            ${form.document?._id
              ? html`<cr-button
                  tone="error"
                  mode="icon"
                  icon="delete"
                  @click=${async () => {
                    await form.document?.remove();
                    resolve(null);
                  }}
                ></cr-button>`
              : nothing}
          </cr-flex>
        </cr-flex>`;
      },
    });
  }

  private _kpfOverview() {
    if (!this._selectedKPF.value || this._config.isLoading) {
      return nothing;
    }

    return html`<cr-flex space="sm" padding="sm">
      ${this._dv()}
      <cr-grid columns="500px 1fr" space="sm" ${css({ alignItems: 'start' })}>
        <cr-card space="sm" padding="md">
          <cr-flex horizontal space-between ${css({ marginBottom: this.spacing.md })}>
            <cr-flex space="sm" ${css({ marginTop: this.spacing.sm })}>
              <cr-text bold ${css({ marginBottom: this.spacing.md })}>Kooperationspartner:</cr-text>
              <cr-text> ${this._selectedKPF.value.school?.name}</cr-text>
              <cr-text>
                ${this._selectedKPF.value.school?.address?.strasse},
                ${this._selectedKPF.value.school?.address?.zipCity?.zip}
                ${this._selectedKPF.value.school?.address?.zipCity?.city}
              </cr-text>
            </cr-flex>
            <cr-button
              mode="icon"
              icon="house"
              href="https://www.google.com/maps/search/${this._selectedKPF.value.school?.address?.strasse}, ${this
                ._selectedKPF.value.school?.address?.zipCity?.zip} ${this._selectedKPF.value.school?.address?.zipCity
                ?.city}"
              target="_blank"
            >
              <cr-tooltip><cr-text>Gehe zu Google Maps</cr-text></cr-tooltip>
            </cr-button>
          </cr-flex>

          <kraefte-component-capacity-courses-popup
            ${css({
              borderBottom: '1px solid rgba(0,0,0,0.1)',
              paddingBottom: this.spacing.sm,
              marginBottom: this.spacing.sm,
            })}
            .kpfID=${this.kpfID.value}
          ></kraefte-component-capacity-courses-popup>

          <chancen-component-card-title> SchuKos </chancen-component-card-title>
          <x-flex ${css({ overflow: 'auto' })}> ${this._employeeTable()} </x-flex>
        </cr-card>
        <cr-flex space="sm">
          <cr-card space="md" padding="md">
            <chancen-component-card-title>
              Schnuppertage
              <chancen-component-context-menu
                ${css({ zoom: 0.8 })}
                slot="right"
                .items=${[
                  { icon: 'add', label: 'Schnuppertag hinzufügen', action: () => void this._showGuestDayDialog() },
                ] satisfies ContextMenuItem[]}
              ></chancen-component-context-menu>
            </chancen-component-card-title>
            ${guard(
              [],
              () =>
                html`<cr-data-view
                  .options=${{
                    class: CapacityGuestDay,
                    view: new View({
                      filter: new AdornisFilter({
                        aspects: [
                          new StringAspect({
                            fieldName: 'KPFID',
                            mode: 'equals',
                            value: `${this._selectedKPF.value?._id}`,
                          }),
                        ],
                      }),
                    }),
                    defaultFieldMapping: {
                      DateTime: (value: DateTime) => value.toFormat('dd.MM.yyyy'),
                      boolean: (value: boolean) => (value ? 'ja' : 'nein'),
                    },
                    onlyShowLastTranslation: true,
                    selectionSet: { ...CapacityGuestDay.allFields },
                    customColumns: [
                      { fieldName: 'day' },
                      {
                        fieldName: 'requestId',
                        columnName: 'Bedarf',
                        selectionSet: { request: { fromWhen: 1, weekdays: 1, type: 1 } },
                        getFieldMapping: e =>
                          e?.request
                            ? `${e.request?.type}, ${e?.request?.fromWhen?.toFormat(
                                'dd.MM.yyyy',
                              )}, ${e?.request?.weekdays?.join(', ')}`
                            : '',
                      },
                      { fieldName: 'vorname' },
                      { fieldName: 'nachname' },
                      { fieldName: 'telefonBewerber' },
                      {
                        fieldName: 'bewerbungslink',
                        getFieldMapping: e =>
                          e.bewerbungslink
                            ? html`<cr-text>
                                <cr-link href=${e.bewerbungslink} target="_blank" @click=${e => e.stopPropagation()}>
                                  Bewerbungslink
                                </cr-link>
                              </cr-text>`
                            : nothing,
                      },
                      {
                        customID: 'actions',
                        columnName: '',
                        getFieldMapping: ent => {
                          return html`<cr-flex horizontal end space="sm">
                            <cr-button
                              mode="icon"
                              icon="check"
                              @click=${async e => {
                                e.preventDefault();
                                e.stopPropagation();
                                await ChromiumDialog.guard(
                                  async () => {
                                    const req = await getByID(
                                      CapacityRequest,
                                      ent.requestId ?? '',
                                    )({
                                      schnuppertagDatum: 1,
                                      vorname: 1,
                                      nachname: 1,
                                      telefonBewerber: 1,
                                      bewerbungslink: 1,
                                    });
                                    if (!req) {
                                      throw new Error('Bedarf nicht gefunden');
                                    }

                                    req.schnuppertagDatum = ent.day;
                                    req.vorname = ent.vorname;
                                    req.nachname = ent.nachname;
                                    req.telefonBewerber = ent.telefonBewerber;
                                    req.bewerbungslink = ent.bewerbungslink;
                                    await req.save();
                                  },
                                  { hasToConfirm: true, confirmText: 'Wirklich auf Bedarf übertragen?' },
                                );
                              }}
                            >
                              <cr-tooltip><cr-text>Auf Bedarf übertragen</cr-text></cr-tooltip>
                            </cr-button>
                            <cr-button
                              mode="icon"
                              icon="delete"
                              tone="error"
                              @click=${async e => {
                                e.preventDefault();
                                e.stopPropagation();
                                await ChromiumDialog.guard(
                                  async () => {
                                    await ent.remove();
                                  },
                                  { hasToConfirm: true, confirmText: 'Wirklich löschen?' },
                                );
                              }}
                            >
                              <cr-tooltip><cr-text>Ablehnen und löschen</cr-text></cr-tooltip>
                            </cr-button>
                          </cr-flex>`;
                        },
                      },
                    ],
                  } satisfies DataViewOptions<typeof CapacityGuestDay>}
                  @entry-clicked=${async (e: CustomEvent<{ entity: CapacityGuestDay }>) => {
                    await this._showGuestDayDialog(e.detail.entity);
                  }}
                ></cr-data-view>`,
            )}
          </cr-card>
          <cr-card space="md" padding="md">
            <chancen-component-card-title>
              Kommentare
              <chancen-component-context-menu
                ${css({ zoom: 0.8 })}
                slot="right"
                .items=${[
                  {
                    icon: 'add',
                    label: 'Kommentar hinzufügen',
                    action: async () => {
                      const comment = await ChromiumDialog.showPopup<string>({
                        content: (resolve, reject, dialogElement) => {
                          let comment = '';
                          return html`<cr-flex space="md" padding="md">
                            <cr-textarea
                              ${css({ width: '400px' })}
                              @value-picked=${(e: ValueEvent<string>) => (comment = e.detail.value)}
                            ></cr-textarea>
                            <cr-button @click=${() => resolve(comment)}>Speichern</cr-button>
                          </cr-flex>`;
                        },
                      });

                      try {
                        await CommentsAPI.create(`CKRÄFTE_KPF_${this._selectedKPF.value?._id}`, comment)();
                        await CapacityAPI.notifyComment(this.kpfID.value, comment ?? '')();
                      } catch (e: any) {
                        ChromiumSnackbar.show(e.message);
                      }
                    },
                  },
                ] satisfies ContextMenuItem[]}
              ></chancen-component-context-menu>
            </chancen-component-card-title>
            ${guard(
              [],
              () =>
                html`<cr-data-view
                  .options=${{
                    class: Comment,
                    view: new View({
                      filter: new AdornisFilter({
                        sortingOrder: ['asc'],
                        aspects: [
                          new StringAspect({
                            fieldName: 'roomID',
                            mode: 'equals',
                            value: `CKRÄFTE_KPF_${this._selectedKPF.value?._id}`,
                          }),
                        ],
                      }),
                    }),
                    defaultFieldMapping: {
                      DateTime: (value: DateTime) => value.toFormat('dd.MM.yyyy HH:mm'),
                      boolean: (value: boolean) => (value ? 'ja' : 'nein'),
                    },
                    onlyShowLastTranslation: true,
                    customColumns: [
                      {
                        fieldName: 'createdAt',
                        columnName: 'von wann',
                        columnWidth: 'max-content',
                        isSortable: false,
                      },
                      {
                        fieldName: 'text',
                        columnName: 'Kommentar',
                        columnWidth: '1fr',
                        isSortable: false,
                        getFieldMapping: e => {
                          const edit = async () => {
                            await ChromiumDialog.showPopup<void>({
                              content: (resolve, reject, dialogElement) => {
                                let comment = e.text;
                                return html`<cr-flex space="md" padding="md">
                                  <cr-textarea
                                    ${css({ width: '400px' })}
                                    .value=${comment}
                                    @value-picked=${(e: ValueEvent<string>) => (comment = e.detail.value)}
                                  ></cr-textarea>
                                  <cr-flex horizontal space="md">
                                    <cr-button
                                      @click=${async () => {
                                        e.text = comment;
                                        await e.save();
                                        await CapacityAPI.notifyComment(this.kpfID.value, comment ?? '')();
                                        resolve();
                                      }}
                                      >Speichern</cr-button
                                    >
                                    <cr-button
                                      tone="error"
                                      @click=${async () => {
                                        await e.remove();
                                        resolve();
                                      }}
                                      >Löschen</cr-button
                                    >
                                  </cr-flex>
                                </cr-flex>`;
                              },
                            });
                          };

                          return html`<cr-text @click=${async () => edit()}>${e.text}</cr-text>`;
                        },
                      },
                      {
                        fieldName: 'user',
                        columnName: 'Autor',
                        columnWidth: 'max-content',
                        selectionSet: { user: { vorname: 1, nachname: 1 } },
                        getFieldMapping: e => `${e.user?.vorname} ${e.user?.nachname}`,
                        isSortable: false,
                      },
                    ],
                  } satisfies DataViewOptions<typeof Comment>}
                ></cr-data-view>`,
            )}
          </cr-card>

          ${this._referenceIDs.value
            ? html`
                <cr-card padding="md">
                  <cr-nospace-toggle-content .label=${'Änderungshistorie'}>
                    ${ChromiumEntityVersionPatchList.render({
                      referenceIDs: this._referenceIDs.value,
                      filterSinglePatches: filterPatches(this._translation),
                      formatValue: formatPatchValue,
                      useAuthorName: true,
                    })}
                  </cr-nospace-toggle-content>
                </cr-card>
              `
            : nothing}
        </cr-flex>
      </cr-grid>
    </cr-flex>`;
  }

  private _dv() {
    const options = buildChancenDataViewOptions<typeof CapacityRequest>(
      {
        class: CapacityRequest,
        operation: (skip, limit, filter) => {
          return CapacityAPI.subscribeByKPF(this.kpfID.value, skip, limit, this._config.value?.view?.filter ?? filter);
        },
        view: this._config.value?.view ?? new View({ filter: new AdornisFilter({}) }),
        onViewChanged: async view => {
          if (!this._config.value) return;
          this._config.value.view = view;
          if (this._config.value._id) await this._config.value.save();
          else await this._config.value.create();
        },
        isColumnSelectable: true,
        isColumnResortable: true,
        isColumnResizable: true,
        isEntryClickable: true,
        defaultFieldMapping: {
          DateTime: (value: DateTime) => value.toFormat('dd.MM.yyyy HH:mm'),
          boolean: (value: boolean) => (value ? 'ja' : 'nein'),
        },
        blacklistColumnSelectionFields: {
          _class: 1,
          createdByID: 1,
          editedByID: 1,
          KPFID: 1,
          linkedUserID: 1,
          KPF: KPF_BLACKLIST,
          createdBy: PERSON_BLACKLIST,
          editedBy: PERSON_BLACKLIST,
          linkedUser: PERSON_BLACKLIST,
        },
        onlyShowLastTranslation: true,
        customColumns: [
          { fieldName: 'fromWhen', getFieldMapping: e => e.fromWhen?.toFormat('dd.MM.yyyy') },
          { fieldName: 'datumChancenSpirit', getFieldMapping: e => e.datumChancenSpirit?.toFormat('dd.MM.yyyy') },
          { fieldName: 'datumEinfuehrungLKK', getFieldMapping: e => e.datumEinfuehrungLKK?.toFormat('dd.MM.yyyy') },
          {
            fieldName: 'bewerbungslink',
            getFieldMapping: e =>
              e.bewerbungslink
                ? html`<cr-text>
                    <cr-link href=${e.bewerbungslink} target="_blank" @click=${e => e.stopPropagation()}>
                      Bewerbungslink
                    </cr-link>
                  </cr-text>`
                : nothing,
          },
          {
            customID: 'history',
            columnName: 'Änderungshistorie',
            getFieldMapping: ent =>
              html`<cr-button
                mode="icon"
                icon="history"
                @click=${async e => {
                  e.stopPropagation();
                  ChromiumDialog.showPopup({
                    content: resolve => {
                      return html`<cr-flex padding="md" space="md" ${css({ minWidth: '500px' })}>
                        <chancen-component-card-title> Änderungshistorie </chancen-component-card-title>
                        <cr-flex ${css({ maxHeight: '500px', overflow: 'auto' })} padding="sm">
                          ${ChromiumEntityVersionPatchList.render({
                            referenceIDs: [ent._id],
                            filterSinglePatches: filterPatches(this._translation),
                            formatValue: formatPatchValue,
                            useAuthorName: true,
                          })}
                        </cr-flex>
                        <cr-button @click=${() => resolve(true)}>Schließen</cr-button>
                      </cr-flex>`;
                    },
                  });
                }}
              ></cr-button>`,
          },
          {
            fieldName: 'editedAt',
            getFieldMapping: e => {
              const editedToday = e.editedAt?.toFormat('dd.MM.yyyy') === DateTime.now().toFormat('dd.MM.yyyy');

              return html`<cr-text
                ${css({
                  color: editedToday ? this.colors.tone.error : 'inherit',
                  fontWeight: editedToday ? 'bold' : 'normal',
                })}
                >${e.editedAt?.toFormat('dd.MM.yyyy HH:mm')}</cr-text
              >`;
            },
          },
        ],
      },
      {
        onFilterChanged: async filter => {
          if (!this._config.value?.view) return;
          this._config.value.view.filter = filter;
          if (this._config.value._id) await this._config.value.save();
          else await this._config.value.create();
        },
        fieldAspectGeneration: {
          hideFieldsWithoutTranslation: true,
          blacklist: {
            _class: 1,
            createdByID: 1,
            editedByID: 1,
            KPFID: 1,
            linkedUserID: 1,
            KPF: KPF_BLACKLIST,
            createdBy: PERSON_BLACKLIST,
            editedBy: PERSON_BLACKLIST,
            linkedUser: PERSON_BLACKLIST,
          },
        },
        overridesForFieldName: {
          type: ListAspectSchema(Object.values(CapacityRequestType), 'Art'),
          weekdays: ListAspectSchema(Object.values(Weekday), 'Wochentage'),
          status: ListAspectSchema(Object.values(CapacityRequestStatus), 'Status'),
        },
      },
    );

    return html`<cr-flex space="md" ${css({ overflow: 'auto' })}>
      <cr-card> ${FilterEditor({ noCollapse: true, options: options.filterOptions })} </cr-card>
      <cr-card space="md" padding="md" ${css({ overflow: 'auto' })}>
        <cr-data-view
          @entry-clicked=${async (e: CustomEvent<{ entity: CapacityRequest }>) => {
            KraefteDialogEditCapacity.showPopup({
              modal: true,
              props: { request: await CapacityRequest.getByID(e.detail.entity._id)({ ...CapacityRequest.allFields }) },
            });
          }}
          .options=${options.dataViewOptions}
        ></cr-data-view>
      </cr-card>
    </cr-flex> `;
  }

  private _handynummerFormat(user: Mitarbeiter) {
    if (user.handynummer && user.handynummerChancenwerk) {
      return `dienstlich: ${user.handynummerChancenwerk} privat: ${user.handynummer}`;
    }

    return `${user.handynummerChancenwerk ?? user.handynummer}`;
  }

  private _emailFormat(user: Mitarbeiter) {
    const email = user.email && user.email.length > 0 ? user.email : null;
    const emailChancenwerk = user.emailChancenwerk && user.emailChancenwerk.length > 0 ? user.emailChancenwerk : null;

    if (email && emailChancenwerk) {
      return `dienstlich: ${emailChancenwerk} privat: ${email}`;
    }

    return `${emailChancenwerk ?? email}`;
  }

  private _employeeTable() {
    if (!this._selectedKPF.value) {
      return nothing;
    }

    // Sort by vorname asc
    (this._schukos.value ?? []).sort((a, b) => (a.vorname ?? '').localeCompare(b.vorname ?? ''));
    (this._stbs.value ?? []).sort((a, b) => (a.vorname ?? '').localeCompare(b.vorname ?? ''));

    const everyone = [...(this._schukos.value ?? []), ...(this._stbs.value ?? [])];

    if (everyone.length === 0) {
      return html`<cr-flex
        horizontal
        crossaxis-center
        space="sm"
        padding="md"
        ${css({ borderRadius: '10px', backgroundColor: this.colors.tone.error.shade(50) })}
      >
        <cr-icon>info</cr-icon> <cr-text>Es sind keine Personen hinterlegt.</cr-text>
      </cr-flex>`;
    }

    const mitarbeiterColumns = [
      {
        columnName: ' ',
        customID: '__link',
        columnWidth: '55px',
        getFieldMapping(entityInstance: Mitarbeiter) {
          const addr = entityInstance.adresse;
          return html`<cr-flex horizontal>
            <cr-button
              mode="icon"
              icon="link"
              href="${projectConfig.get('RAUM_URL').slice(0, -1)}/edit/person/employee/${entityInstance._id}"
              target="_blank"
            >
              <cr-tooltip><cr-text>Gehe zu Stammdaten</cr-text></cr-tooltip>
            </cr-button>
            <cr-button
              mode="icon"
              icon="house"
              href="https://www.google.com/maps/search/${addr?.strasse}, ${addr?.zipCity?.zip} ${addr?.zipCity?.city}"
              target="_blank"
            >
              <cr-tooltip><cr-text>Gehe zu Google Maps</cr-text></cr-tooltip>
            </cr-button>
          </cr-flex>`;
        },
      },
      {
        columnName: 'Type',
        customID: 'type',
        columnWidth: 'max-content',
        renderHeader: this._smallerHeader('Type'),
        getFieldMapping: (entityInstance: Mitarbeiter) => {
          const inSchukos = (this._schukos.value ?? []).some(s => s._id === entityInstance._id);
          const inStbs = (this._stbs.value ?? []).some(s => s._id === entityInstance._id);
          return html`<cr-text>${inSchukos ? 'Schuko' : inStbs ? 'STB' : 'Unknown'}</cr-text>`;
        },
      },
      {
        columnName: 'Vorname',
        fieldName: 'vorname',
        columnWidth: 'max-content',
        renderHeader: this._smallerHeader('Vorname'),
        getFieldMapping: (entityInstance: Mitarbeiter) => {
          return html`<cr-text ellipsis .value=${entityInstance.vorname} ${css({ width: '80px' })}>
            ${(entityInstance.vorname?.length ?? 0) > 10
              ? html`<cr-tooltip><cr-text>${entityInstance.vorname}</cr-text></cr-tooltip>`
              : nothing}
          </cr-text>`;
        },
      },
      {
        columnName: 'Nachname',
        fieldName: 'nachname',
        columnWidth: '1fr',
        renderHeader: this._smallerHeader('Nachname'),
        getFieldMapping: (entityInstance: Mitarbeiter) => {
          if (!entityInstance.nachname) {
            return entityInstance.email;
          }
          return entityInstance.nachname;
        },
      },
      {
        columnName: 'Kontakt',
        customID: '__contact',
        columnWidth: 'max-content',
        renderHeader: this._smallerHeader('Kontakt'),
        getFieldMapping: (entityInstance: Mitarbeiter) => {
          return html`<cr-flex horizontal crossaxis-center space="sm">
            <chancen-component-icon-spoiler icon="email" hint="E-Mail einblenden">
              ${this._emailFormat(entityInstance)}
            </chancen-component-icon-spoiler>
            <chancen-component-icon-spoiler icon="call" hint="Handynummer einblenden">
              ${this._handynummerFormat(entityInstance)}
            </chancen-component-icon-spoiler>
          </cr-flex>`;
        },
      },
    ];

    return html` <cr-data-view
      ${css({ width: '100%', maxHeight: `${everyone.length * 33 + 27}px` })}
      .options=${{
        class: Mitarbeiter,
        paginationComplete: true,
        isEntryClickable: false,
        operation: (skip, limit) => {
          return fields => {
            if (skip > 0) {
              return of([]);
            }
            return of(everyone);
          };
        },
        onlyShowLastTranslation: true,
        customColumns: mitarbeiterColumns,
      } as DataViewOptions<typeof Mitarbeiter>}
      flex
    >
    </cr-data-view>`;
  }
}
